import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import track, { TRACKING_TYPES } from "../../../Utils/SupabaseServices/Track";
import trackTemplate, {
  TEMPLATE_TRACKING_TYPES,
} from "../../../Utils/SupabaseServices/TrackTemplate";
import {
  TEMPLATE_ANALYTICS_EVENTS,
  templateRouteName,
  type TemplateEntityType,
} from "../../../Utils/SupabaseServices/templateAnalyticsEvents";
import {
  legacyLibAnalyticsPathname,
  shouldMirrorLegacyLibAnalytics,
} from "../../../Utils/analytics/legacyLibAnalyticsPath";
import { canonicalWebsiteAnalyticsPageKey } from "../WebsiteAnalytics/websiteAnalyticsMerge";
import { parsePublicWebsiteSubpath } from "./websitePublicTemplatePath";
import type { TemplateContentView, TemplateShopView } from "./types";

export type TemplateAnalyticsContextValue = {
  enabled: boolean;
  userId: string;
  username: string;
  routeName: string;
  pageKey: string;
  
  trackPageView: (opts?: {
    pageKey?: string;
    pathname?: string;
    details?: Record<string, unknown>;
  }) => void;
  trackNavClick: (label: string, targetPageKey: string) => void;
  trackPostClick: (payload: {
    postId: string;
    mediaUrl?: string;
    platform?: string;
    title?: string;
    name?: string;
    sectionId?: string;
    sectionType?: string;
    /** Override default "post" (e.g. links-section → "link"). */
    entityType?: TemplateEntityType;
    carouselSlug?: string;
    contentView?: TemplateContentView;
    contentTab?: string;
    component?: Record<string, unknown> | null;
  }) => void;
  trackShopClick: (payload: {
    postId: string;
    mediaUrl?: string;
    platform?: string;
    name?: string;
    sectionId?: string;
    carouselSlug?: string;
    shopView?: TemplateShopView;
    contentCategory?: string;
    categoryPrimary?: string;
    categorySecondary?: string;
    categoryBrand?: string;
    component?: Record<string, unknown> | null;
  }) => void;
  trackQuickLink: (payload: {
    linkId: string;
    title: string;
    url: string;
  }) => void;
  trackBannerCta: (payload: { which: string; label: string; url: string }) => void;
  trackCarouselTab: (payload: {
    slug: string;
    label: string;
    pageKey?: string;
  }) => void;
  trackContentTab: (payload: {
    slug: string;
    label: string;
    contentView?: TemplateContentView;
    sourcePageKey?: string;
    isSeriesTab?: boolean;
  }) => void;
  trackContentViewSwitch: (payload: { contentView: TemplateContentView }) => void;
  trackShopTab: (payload: {
    tabKind: string;
    label: string;
    categorySlug?: string;
  }) => void;
  trackShopBrowse: (payload: {
    level: "primary" | "secondary" | "sub" | "brand" | "clear";
    label: string;
    primarySlug?: string | null;
    secondarySlug?: string | null;
    subSlug?: string | null;
    brandSlug?: string | null;
  }) => void;
  trackJournalPost: (payload: {
    postId: string;
    slug: string;
    title: string;
  }) => void;
  trackSearch: (query: string, searchType?: string) => void;
  trackSocialLink: (payload: {
    platform: string;
    url: string;
    sectionId?: string;
    sectionType?: string;
  }) => void;
  trackHighlightClick: (payload: {
    label: string;
    platformKey?: string;
    sectionId?: string;
  }) => void;
  trackSectionEvent: (
    eventName: string,
    payload?: {
      sectionId?: string;
      sectionType?: string;
      entityType?: TemplateEntityType;
      entityId?: string;
      entityLabel?: string;
      details?: Record<string, unknown>;
      /** Override the tracking type. Defaults to EVENT. Use RENDER for passive
       *  render/impression events so they don't pollute click-count dashboards. */
      eventType?: TEMPLATE_TRACKING_TYPES;
    },
  ) => void;
};

const TemplateAnalyticsContext =
  createContext<TemplateAnalyticsContextValue | null>(null);

const TRAFFIC_SOURCE_SESSION_KEY = "sb_template_analytics_source";

/**
 * Prefer live `?source=` (and remember it for the tab). SPA navigations often
 * drop query params, so later page/nav/click events still attribute correctly.
 */
export function readTrafficSource(): string {
  if (typeof window === "undefined") return "";
  const fromUrl =
    new URLSearchParams(window.location.search).get("source")?.trim() || "";
  if (fromUrl) {
    try {
      sessionStorage.setItem(TRAFFIC_SOURCE_SESSION_KEY, fromUrl);
    } catch {
      // private mode / quota
    }
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(TRAFFIC_SOURCE_SESSION_KEY) || "";
  } catch {
    return "";
  }
}

export function TemplateAnalyticsProvider({
  enabled,
  userId,
  username,
  pageKey,
  publicWebsitePathRest,
  shopBrowse,
  children,
}: {
  enabled: boolean;
  userId: string;
  username: string;
  pageKey: string;
  publicWebsitePathRest?: string;
  shopBrowse?: Record<string, unknown>;
  children: React.ReactNode;
}) {
  const routeName = useMemo(() => templateRouteName(username), [username]);
  const lastPageViewKeyRef = useRef("");

  const baseExtras = useCallback(
    (overrides?: Partial<Parameters<typeof trackTemplate>[2]>) => ({
      user_id: userId,
      username,
      route_name: routeName,
      pathname:
        typeof window !== "undefined" ? window.location.pathname : "",
      source: readTrafficSource(),
      page_key: pageKey,
      shop_browse: shopBrowse,
      title: "Website Template",
      ...overrides,
    }),
    [userId, username, routeName, pageKey, shopBrowse],
  );

  const trackPageView = useCallback(
    (opts?: {
      pageKey?: string;
      pathname?: string;
      details?: Record<string, unknown>;
    }) => {
      if (!enabled || !userId) return;
      const pk = opts?.pageKey || pageKey;
      const pathname =
        opts?.pathname ||
        (typeof window !== "undefined" ? window.location.pathname : "");
      const dedupeKey = canonicalWebsiteAnalyticsPageKey(pk);
      if (lastPageViewKeyRef.current === dedupeKey) return;
      lastPageViewKeyRef.current = dedupeKey;

      const parsed = parsePublicWebsiteSubpath(publicWebsitePathRest);
      void trackTemplate(
        pathname || TEMPLATE_ANALYTICS_EVENTS.PAGE_VIEW,
        TEMPLATE_TRACKING_TYPES.PAGE,
        baseExtras({
          page_key: pk,
          pathname,
          details: {
            ...parsed,
            ...(opts?.details || {}),
          },
        }),
      );
    },
    [enabled, userId, pageKey, publicWebsitePathRest, baseExtras],
  );

  const trackNavClick = useCallback(
    (label: string, targetPageKey: string) => {
      if (!enabled || !userId) return;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.NAV_CLICK,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          entity_type: "nav",
          entity_id: targetPageKey,
          entity_label: label,
          details: { target_page_key: targetPageKey },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackPostClick = useCallback(
    (payload: {
      postId: string;
      mediaUrl?: string;
      platform?: string;
      title?: string;
      name?: string;
      sectionId?: string;
      sectionType?: string;
      entityType?: TemplateEntityType;
      carouselSlug?: string;
      contentView?: TemplateContentView;
      contentTab?: string;
      /** Stayuptodate row — keeps duplicate carousel titles from sharing buckets. */
      component?: Record<string, unknown> | null;
    }) => {
      if (!enabled || !userId || !payload.postId) return;
      const identity = payload.component
        ? {
            component_id:
              payload.component.id != null
                ? String(payload.component.id)
                : undefined,
            unique_id:
              payload.component.unique_id != null
                ? String(payload.component.unique_id)
                : undefined,
          }
        : {};
      const carouselSlug =
        payload.carouselSlug ||
        identity.component_id ||
        identity.unique_id ||
        undefined;
      const isLinksSection =
        String(payload.platform || "").toLowerCase() === "links-section";
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.POST_CLICK,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          entity_type:
            payload.entityType || (isLinksSection ? "link" : "post"),
          entity_id: payload.postId,
          entity_label: isLinksSection
            ? payload.name || payload.title || payload.postId
            : payload.title || payload.name || payload.postId,
          section_id: payload.sectionId,
          section_type:
            payload.sectionType || (isLinksSection ? "Links" : undefined),
          carousel_slug: carouselSlug,
          details: {
            post_id: payload.postId,
            media_url: payload.mediaUrl,
            platform: payload.platform,
            // Post title only — never fall back to carousel/component name
            // (that polluted Website Analytics → Top viewed Post / Detail).
            title: payload.title,
            name: payload.name,
            content_view: payload.contentView,
            content_tab: payload.contentTab,
            ...(identity.component_id
              ? { component_id: identity.component_id }
              : {}),
            ...(identity.unique_id ? { unique_id: identity.unique_id } : {}),
          },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackShopClick = useCallback(
    (payload: {
      postId: string;
      mediaUrl?: string;
      platform?: string;
      name?: string;
      sectionId?: string;
      carouselSlug?: string;
      shopView?: TemplateShopView;
      contentCategory?: string;
      categoryPrimary?: string;
      categorySecondary?: string;
      categoryBrand?: string;
      component?: Record<string, unknown> | null;
    }) => {
      if (!enabled || !userId || !payload.postId) return;
      const identity = payload.component
        ? {
            component_id:
              payload.component.id != null
                ? String(payload.component.id)
                : undefined,
            unique_id:
              payload.component.unique_id != null
                ? String(payload.component.unique_id)
                : undefined,
          }
        : {};
      const carouselSlug =
        payload.carouselSlug ||
        identity.component_id ||
        identity.unique_id ||
        undefined;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.SHOP_CLICK,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          // Align with FeedControls / StayUpToDate dual-write (product, not shop_button).
          entity_type: "product",
          entity_id: payload.postId,
          entity_label: payload.name || payload.postId,
          section_id: payload.sectionId,
          carousel_slug: carouselSlug,
          details: {
            post_id: payload.postId,
            media_url: payload.mediaUrl,
            platform: payload.platform,
            name: payload.name,
            shop_view: payload.shopView,
            content_category: payload.contentCategory,
            category_primary: payload.categoryPrimary,
            category_secondary: payload.categorySecondary,
            category_brand: payload.categoryBrand,
            ...(identity.component_id
              ? { component_id: identity.component_id }
              : {}),
            ...(identity.unique_id ? { unique_id: identity.unique_id } : {}),
          },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackQuickLink = useCallback(
    (payload: { linkId: string; title: string; url: string }) => {
      if (!enabled || !userId) return;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.QUICK_LINK,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          entity_type: "link",
          entity_id: payload.linkId,
          entity_label: payload.title,
          section_id: "links",
          section_type: "Links",
          details: { url: payload.url, title: payload.title },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackBannerCta = useCallback(
    (payload: { which: string; label: string; url: string }) => {
      if (!enabled || !userId) return;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.BANNER_CTA,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          entity_type: "banner",
          entity_id: payload.which,
          entity_label: payload.label,
          section_id: payload.which === "hero" ? "heroBanner" : "promoBanner",
          section_type: "Banner",
          details: { url: payload.url, cta: payload.label },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackCarouselTab = useCallback(
    (payload: { slug: string; label: string; pageKey?: string }) => {
      if (!enabled || !userId) return;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.CAROUSEL_TAB,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          page_key: payload.pageKey || pageKey,
          entity_type: "carousel_tab",
          entity_id: payload.slug,
          entity_label: payload.label,
          carousel_slug: payload.slug,
          details: { slug: payload.slug, label: payload.label },
        }),
      );
    },
    [enabled, userId, pageKey, baseExtras],
  );

  const trackContentTab = useCallback(
    (payload: {
      slug: string;
      label: string;
      contentView?: TemplateContentView;
      sourcePageKey?: string;
      isSeriesTab?: boolean;
    }) => {
      if (!enabled || !userId) return;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.CONTENT_TAB,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          page_key: "content",
          entity_type: payload.isSeriesTab ? "content-series" : "content_tab",
          entity_id: payload.slug,
          entity_label: payload.label,
          section_id: "contentGrid",
          section_type: "ContentGrid",
          details: {
            slug: payload.slug,
            label: payload.label,
            content_view: payload.contentView,
            source_page_key: payload.sourcePageKey,
            is_series_tab: payload.isSeriesTab,
          },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackContentViewSwitch = useCallback(
    (payload: { contentView: TemplateContentView }) => {
      if (!enabled || !userId) return;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.CONTENT_VIEW_SWITCH,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          page_key: "content",
          entity_type: "content_tab",
          entity_id: payload.contentView,
          entity_label: payload.contentView === "series" ? "Series" : "Latest",
          section_id: "contentGrid",
          section_type: "ContentGrid",
          details: { content_view: payload.contentView },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackShopTab = useCallback(
    (payload: {
      tabKind: string;
      label: string;
      categorySlug?: string;
    }) => {
      if (!enabled || !userId) return;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.SHOP_TAB,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          page_key: "shop",
          entity_type: "shop_category",
          entity_id: payload.categorySlug || payload.tabKind,
          entity_label: payload.label,
          section_id: "productGrid",
          section_type: "ProductGrid",
          details: {
            tab_kind: payload.tabKind,
            label: payload.label,
            category_slug: payload.categorySlug,
          },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackShopBrowse = useCallback(
    (payload: {
      level: "primary" | "secondary" | "sub" | "brand" | "clear";
      label: string;
      primarySlug?: string | null;
      secondarySlug?: string | null;
      subSlug?: string | null;
      brandSlug?: string | null;
    }) => {
      if (!enabled || !userId) return;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.SHOP_BROWSE,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          page_key: "shop",
          entity_type: "shop_category",
          entity_id:
            payload.brandSlug ||
            payload.subSlug ||
            payload.secondarySlug ||
            payload.primarySlug ||
            payload.level,
          entity_label: payload.label,
          section_id: "productGrid",
          section_type: "ProductGrid",
          details: {
            browse_level: payload.level,
            label: payload.label,
            primary_slug: payload.primarySlug,
            secondary_slug: payload.secondarySlug,
            sub_slug: payload.subSlug,
            brand_slug: payload.brandSlug,
          },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  // Same post can be wired to two anchors (row + READ). Dedupe so one open
  // cannot insert two "Journal Post Click" rows (~2× in Blog analytics).
  const lastJournalPostTrackRef = useRef({ key: "", at: 0 });
  const trackJournalPost = useCallback(
    (payload: { postId: string; slug: string; title: string }) => {
      if (!enabled || !userId) return;
      const key = String(payload.postId || payload.slug || "").trim();
      if (!key) return;
      const now = Date.now();
      if (
        lastJournalPostTrackRef.current.key === key &&
        now - lastJournalPostTrackRef.current.at < 1500
      ) {
        return;
      }
      lastJournalPostTrackRef.current = { key, at: now };
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.JOURNAL_POST,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          page_key: "journal",
          entity_type: "journal",
          entity_id: key,
          entity_label: payload.title,
          section_id: "journalGrid",
          section_type: "JournalGrid",
          details: payload,
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackSearch = useCallback(
    (query: string, searchType = "searchbar") => {
      if (!enabled || !userId) return;
      const q = String(query || "").trim().toLowerCase();
      if (!q) return;
      void trackTemplate(
        `${searchType} search`,
        TEMPLATE_TRACKING_TYPES.SEARCH,
        baseExtras({
          page_key: "search",
          entity_type: "search",
          entity_label: q,
          details: { query: q, search_type: searchType },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackSocialLink = useCallback(
    (payload: {
      platform: string;
      url: string;
      sectionId?: string;
      sectionType?: string;
    }) => {
      if (!enabled || !userId) return;
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : "";
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.SOCIAL_LINK,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          entity_type: "social",
          entity_id: payload.platform,
          entity_label: payload.platform,
          section_id: payload.sectionId || "bio",
          section_type: payload.sectionType || "Bio",
          details: {
            platform: payload.platform,
            social: payload.platform,
            url: payload.url,
            link: payload.url,
          },
        }),
      );
      // Classic LiB Social Icons RPC reads `analytics` with details.social.
      if (shouldMirrorLegacyLibAnalytics(pathname, pageKey)) {
        void track("Redirect to social link", TRACKING_TYPES.EVENT, {
          pathname: legacyLibAnalyticsPathname(username),
          details: {
            social: payload.platform,
            link: payload.url || "",
          },
          user_id: userId,
          title: "Stay Up To Date",
        });
      }
    },
    [enabled, userId, username, pageKey, baseExtras],
  );

  const trackHighlightClick = useCallback(
    (payload: { label: string; platformKey?: string; sectionId?: string }) => {
      if (!enabled || !userId) return;
      void trackTemplate(
        TEMPLATE_ANALYTICS_EVENTS.HIGHLIGHT_CLICK,
        TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          // Not "social" — Home Social panel filters entity_type=social for Bio icons only.
          entity_type: "highlight",
          entity_id: payload.platformKey || payload.label,
          entity_label: payload.label,
          section_id: payload.sectionId || "highlights",
          section_type: "Highlights",
          details: { platform_key: payload.platformKey, label: payload.label },
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const trackSectionEvent = useCallback(
    (
      eventName: string,
      payload?: {
        sectionId?: string;
        sectionType?: string;
        entityType?: TemplateEntityType;
        entityId?: string;
        entityLabel?: string;
        details?: Record<string, unknown>;
        eventType?: TEMPLATE_TRACKING_TYPES;
      },
    ) => {
      if (!enabled || !userId) return Promise.resolve();
      return trackTemplate(
        eventName,
        payload?.eventType ?? TEMPLATE_TRACKING_TYPES.EVENT,
        baseExtras({
          section_id: payload?.sectionId,
          section_type: payload?.sectionType,
          entity_type: payload?.entityType,
          entity_id: payload?.entityId,
          entity_label: payload?.entityLabel,
          details: payload?.details,
        }),
      );
    },
    [enabled, userId, baseExtras],
  );

  const value = useMemo(
    () => ({
      enabled,
      userId,
      username,
      routeName,
      pageKey,
      trackPageView,
      trackNavClick,
      trackPostClick,
      trackShopClick,
      trackQuickLink,
      trackBannerCta,
      trackCarouselTab,
      trackContentTab,
      trackContentViewSwitch,
      trackShopTab,
      trackShopBrowse,
      trackJournalPost,
      trackSearch,
      trackSocialLink,
      trackHighlightClick,
      trackSectionEvent,
    }),
    [
      enabled,
      userId,
      username,
      routeName,
      pageKey,
      trackPageView,
      trackNavClick,
      trackPostClick,
      trackShopClick,
      trackQuickLink,
      trackBannerCta,
      trackCarouselTab,
      trackContentTab,
      trackContentViewSwitch,
      trackShopTab,
      trackShopBrowse,
      trackJournalPost,
      trackSearch,
      trackSocialLink,
      trackHighlightClick,
      trackSectionEvent,
    ],
  );

  return (
    <TemplateAnalyticsContext.Provider value={value}>
      {children}
    </TemplateAnalyticsContext.Provider>
  );
}

export function useTemplateAnalytics(): TemplateAnalyticsContextValue | null {
  return useContext(TemplateAnalyticsContext);
}

export function useTemplateAnalyticsOptional(): TemplateAnalyticsContextValue | null {
  return useContext(TemplateAnalyticsContext);
}
